import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MessagesSquare, Store, StickyNote } from "lucide-react";
import { Forums } from "@/components/community/Forums";
import { Wall } from "@/components/community/Wall";
import { useLang } from "@/lib/i18n";

const SECTIONS = [["foros", MessagesSquare], ["pared", StickyNote], ["mercado", Store]];

/** Landing: meetup photo, the community intro and three floating slime buttons. */
const Landing = () => {
  const { t } = useLang();
  return (
    <main className="com-landing" data-testid="community-landing">
      <img src="/comunidad/reunion.jpg" alt={t("com.photoAlt")} className="com-landing-img" />
      <div className="com-landing-shade" />
      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-6xl flex-col justify-center px-5 pb-20 pt-32 md:px-10">
        <p className="mono text-[11px] uppercase tracking-[0.22em] text-[var(--ink)]/80">{t("nav.community")}</p>
        <h1 className="font-display mt-4 text-5xl leading-[0.95] sm:text-6xl lg:text-7xl">{t("com.l1")}<br /><em>{t("com.l2")}</em></h1>
        <p className="mt-6 max-w-2xl text-sm text-[var(--ink)]/85 md:text-base">{t("com.intro")}</p>
        <div className="mt-14 flex flex-col items-center justify-center gap-5 sm:flex-row sm:gap-8" data-testid="community-landing-buttons">
          {SECTIONS.map(([k, Icon], i) => {
            const inner = (
              <>
                <Icon size={26} />
                <span className="font-display text-3xl leading-none">{t(`com.tab.${k}`)}</span>
                {k === "mercado" && <span className="mono text-[10px] uppercase tracking-[0.18em] text-[var(--muted)]">{t("com.marketSoon")}</span>}
              </>
            );
            return (
              <motion.div key={k} initial={{ opacity: 0.3, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.12, duration: 0.5 }}>
                {k === "mercado"
                  ? <div className="slime-btn slime-btn-off" style={{ animationDelay: `${i * 0.6}s` }} aria-disabled="true" data-testid="community-btn-mercado">{inner}</div>
                  : <Link to={`/comunidad?tab=${k}`} className="slime-btn" style={{ animationDelay: `${i * 0.6}s` }} data-testid={`community-btn-${k}`}>{inner}</Link>}
              </motion.div>
            );
          })}
        </div>
      </div>
    </main>
  );
};

/** Floating top-right switcher shown inside each section. */
const FloatingMenu = ({ tab }) => {
  const { t } = useLang();
  return (
    <nav className="com-float-menu" data-testid="community-tabs">
      {SECTIONS.map(([k, Icon]) => (k === "mercado" ? (
        <span key={k} className="com-float-item opacity-60" title={t("com.marketSoon")} aria-disabled="true" data-testid="community-tab-mercado"><Icon size={14} /> {t(`com.tab.${k}`)}</span>
      ) : (
        <Link key={k} to={`/comunidad?tab=${k}`} replace className={`com-float-item ${tab === k ? "com-float-on" : ""}`} aria-current={tab === k ? "page" : undefined} data-testid={`community-tab-${k}`}>
          <Icon size={14} /> {t(`com.tab.${k}`)}
        </Link>
      )))}
    </nav>
  );
};

export default function Community() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const tab = ["foros", "pared"].includes(params.get("tab")) ? params.get("tab") : null;
  if (!tab) return <Landing />;
  return (
    <main className="px-5 pb-28 pt-40 md:px-10 md:pt-32" data-testid="community-page">
      <FloatingMenu tab={tab} />
      <div className="mx-auto max-w-6xl">
        <Link to="/comunidad" className="mono text-[11px] uppercase tracking-[0.22em] text-[var(--muted)] hover:text-[var(--green)]" data-testid="community-home-link">{t("nav.community")}</Link>
        <h1 className="font-display mt-3 text-3xl leading-tight sm:text-4xl">{t("com.l1")} <em>{t("com.l2")}</em></h1>
        <p className="mt-3 max-w-2xl text-sm text-[var(--muted)]">{t("com.intro")}</p>
        <div className="mt-10">
          {tab === "foros" && <Forums />}
          {tab === "pared" && <Wall />}
        </div>
      </div>
    </main>
  );
}
